// Small 3D scene pieces used by the builder (lighting environments, light marker, back-view check). Moved out of App.js unchanged.
import * as THREE from 'three'
import { useEffect, useMemo, useRef } from 'react'
import { useFrame, useThree } from '@react-three/fiber'
import { Environment, Lightformer } from '@react-three/drei'
import { METAL_ENV } from './materials'

export function CameraAngleChecker({ cameraRef, groupRef, setIsViewingBack }) {
  const lastStateRef = useRef(false)

  useFrame(() => {
    if (!cameraRef.current || !groupRef.current) return

    // Get camera position in world space
    const cameraWorldPos = new THREE.Vector3()
    cameraRef.current.getWorldPosition(cameraWorldPos)

    // Get mic group position in world space
    const groupWorldPos = new THREE.Vector3()
    groupRef.current.getWorldPosition(groupWorldPos)

    // Calculate direction from mic to camera (in world space)
    const cameraDirection = new THREE.Vector3().subVectors(cameraWorldPos, groupWorldPos).normalize()

    // Z-axis detection: negative Z means viewing the back (XLR connector side)
    const isBack = cameraDirection.z < 0

    // Only update state when it changes
    if (isBack !== lastStateRef.current) {
      lastStateRef.current = isBack
      setIsViewingBack(isBack)
    }
  })
  return null
}

// Positions for the shadow-casting lights that stand in for the overhead softbox (see the Canvas)
export function softboxLights(s) {
  const n = Math.max(1, Math.round(s.keyCount))
  const fwd = s.keyForward ?? 3
  const h = s.keyHeight ?? 10
  if (n === 1) return [[0, h, fwd]]
  if (s.keyLayout === 1) {
    // Stacked front to back only: softens the shadow edge in the elevation direction (stem, ledges)
    return Array.from({ length: n }, (_, i) => [0, h, fwd + (i / (n - 1) - 0.5) * s.keySpread])
  }
  const spread = s.keySpread
  const pts = []
  const side = Math.ceil(Math.sqrt(n))
  for (let i = 0; i < n; i++) {
    const gx = (i % side) / Math.max(1, side - 1) - 0.5
    const gz = Math.floor(i / side) / Math.max(1, side - 1) - 0.5
    pts.push([gx * spread * 2, h, fwd + gz * spread])
  }
  return pts
}

export function StudioEnvironment({ settings }) {
  const s = settings
  const rad = (s.bounceAngle * Math.PI) / 180
  const bx = s.bounceDistance * Math.sin(rad)
  const bz = s.bounceDistance * Math.cos(rad)
  const ex = s.exposure
  const room = s.roomLevel * ex
  const key = [ex, s.overheadIntensity, s.overheadWidth, s.overheadDepth, s.overheadHeight, s.overheadForward, s.bounceIntensity, s.bounceAngle, s.bounceDistance, s.bounceWidth, s.bounceHeight, room].join('|')
  return (
    <Environment key={key} frames={1} resolution={512}>
      {/* White seamless room */}
      <color attach="background" args={[room, room, room]} />
      {/* Overhead soft panel */}
      <Lightformer
        form="rect"
        intensity={s.overheadIntensity * ex}
        position={[0, s.overheadHeight, s.overheadForward]}
        rotation={[Math.PI / 2, 0, 0]}
        scale={[s.overheadWidth, s.overheadDepth]}
      />
      {/* Bounce cards, angled toward the mic */}
      <Lightformer form="rect" intensity={s.bounceIntensity * ex} position={[-bx, 0, bz]} scale={[s.bounceWidth, s.bounceHeight]} target={[0, 0, 0]} />
      <Lightformer form="rect" intensity={s.bounceIntensity * ex} position={[bx, 0, bz]} scale={[s.bounceWidth, s.bounceHeight]} target={[0, 0, 0]} />
    </Environment>
  )
}

export function MetalEnvironment({ settings }) {
  const gl = useThree((st) => st.gl)
  const s = settings
  const key = [s.exposure, s.metalRoom, s.metalFront, s.metalBox, s.metalBoxW, s.metalBoxH, s.metalBoxX, s.metalBoxY, s.overheadIntensity, s.overheadWidth, s.overheadDepth, s.overheadHeight, s.overheadForward, s.bounceIntensity, s.bounceAngle, s.bounceDistance, s.bounceWidth, s.bounceHeight].join('|')
  useEffect(() => {
    const ex = s.exposure
    const room = (s.metalRoom ?? 0.5) * ex
    const sc = new THREE.Scene()
    sc.background = new THREE.Color(room, room, room)
    const panel = (intensity, w, h, pos, target, rotX) => {
      const m = new THREE.Mesh(
        new THREE.PlaneGeometry(1, 1),
        new THREE.MeshBasicMaterial({ color: new THREE.Color(intensity, intensity, intensity), side: THREE.DoubleSide, toneMapped: false })
      )
      m.scale.set(w, h, 1)
      m.position.set(...pos)
      if (target) m.lookAt(...target)
      else m.rotation.x = rotX
      sc.add(m)
    }
    const rad = (s.bounceAngle * Math.PI) / 180
    const bx = s.bounceDistance * Math.sin(rad)
    const bz = s.bounceDistance * Math.cos(rad)
    panel(s.overheadIntensity * ex, s.overheadWidth, s.overheadDepth, [0, s.overheadHeight, s.overheadForward], null, Math.PI / 2)
    // the camera side of the studio (photographer, white walls): what a front-facing metal face mirrors
    if (s.metalFront) panel(s.metalFront * ex, 7, 6, [0, 0.6, 7], [0, 0, 0])
    // the photographer's softbox: a brighter panel inside that fill. Its edges and falloff are what break up
    // the reflection on hand-polished brass (bright and darker zones across a flat plate)
    if (s.metalBox) panel(s.metalBox * ex, s.metalBoxW ?? 2, s.metalBoxH ?? 3, [s.metalBoxX ?? 0.6, s.metalBoxY ?? 1, 6.9], [0, 0, 0])
    panel(s.bounceIntensity * ex, s.bounceWidth, s.bounceHeight, [-bx, 0, bz], [0, 0, 0])
    panel(s.bounceIntensity * ex, s.bounceWidth, s.bounceHeight, [bx, 0, bz], [0, 0, 0])
    const pmrem = new THREE.PMREMGenerator(gl)
    const rt = pmrem.fromScene(sc, 0, 0.1, 100)
    pmrem.dispose()
    METAL_ENV.target.environment = rt.texture
    sc.traverse((o) => {
      if (o.isMesh) {
        o.geometry.dispose()
        o.material.dispose()
      }
    })
    return () => {
      if (METAL_ENV.target.environment === rt.texture) METAL_ENV.target.environment = null
      rt.dispose()
    }
  }, [gl, key])
  return null
}

export function LightMarker({ position }) {
  const mesh = useRef()
  const lightMaterial = useMemo(() => new THREE.MeshBasicMaterial({ color: 0xffff00 }), [])
  return (
    <mesh ref={mesh} position={position} material={lightMaterial}>
      <sphereGeometry args={[0.1, 16, 16]} />
    </mesh>
  )
}


// Soft light from behind for the back view. It only reaches surfaces facing the back, and it fades in only
// as the camera moves behind the mic (off for the front, side and three-quarter views, full at the back).
export function BackFill({ cameraRef, groupRef, intensity, height = 4 }) {
  const light = useRef()
  const v = useMemo(() => ({ cam: new THREE.Vector3(), mic: new THREE.Vector3() }), [])
  useFrame(() => {
    const l = light.current
    const cam = cameraRef.current
    if (!l || !cam) return
    cam.getWorldPosition(v.cam)
    if (groupRef.current) groupRef.current.getWorldPosition(v.mic)
    else v.mic.set(0, 0, 0)
    const back = -v.cam.sub(v.mic).normalize().z // 1 straight behind, 0 at the side, negative in front
    const t = Math.min(1, Math.max(0, (back - 0.1) / 0.6))
    l.intensity = intensity * t * t * (3 - 2 * t)
    l.target.position.copy(v.mic)
    l.target.updateMatrixWorld()
  })
  return <directionalLight ref={light} position={[0, height, -10]} intensity={0} color="#ffffff" />
}
